import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api';
import type { User, LoginFormData, RegisterFormData } from '@/types';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Resolves to { mfaRequired: true } when a second factor is needed. */
  login: (data: LoginFormData) => Promise<{ mfaRequired?: boolean }>;
  verifyMfa: (code: string) => Promise<void>;
  register: (data: RegisterFormData) => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: authApi.getCurrentUser,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.clear();
    },
  });

  const login = useCallback(
    async (data: LoginFormData) => {
      const result = await authApi.login(data);
      if ('user' in result) {
        // Session established — refresh the current-user query.
        queryClient.invalidateQueries({ queryKey: ['auth'] });
        return {};
      }
      // No session yet; the caller must complete the MFA challenge.
      return { mfaRequired: true };
    },
    [queryClient]
  );

  const verifyMfa = useCallback(
    async (code: string) => {
      await authApi.verifyMfa(code);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    [queryClient]
  );

  const register = useCallback(
    async (data: RegisterFormData) => {
      await registerMutation.mutateAsync(data);
    },
    [registerMutation]
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
    window.location.href = '/login';
  }, [logoutMutation]);

  const value: AuthContextValue = {
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading,
    login,
    verifyMfa,
    register,
    logout,
    refetch: () => refetch(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
